// Response shapes of /public/teknisk/akseldrift/* (backend/app/schemas/teknisk.py)

export type Drive = '2wd' | '4wd'

export const DRIVES: Drive[] = ['2wd', '4wd']

export interface PeriodInfo {
  label: string
  start_date: string
  end_date: string
}

/** The period asked for and the same period a year earlier */
export type Periods = { current: PeriodInfo; previous: PeriodInfo }

export interface AkseldriftStats {
  akseldrift: Drive
  count: number
  share: number | null
  previous_count: number | null
  previous_share: number | null
  share_change_pp: number | null
  leasing_count: number
  leasing_share: number | null
  loan_base: number
  loan_count: number
  loan_share: number | null
}

export interface TopModel {
  master_model_id: number
  model: string
  brand: string
  count: number
  share: number | null
}

export interface TopBrand {
  brand: string
  count: number
  share: number | null
}

export interface AkseldriftOversikt {
  output_mode: string
  periods: { current: PeriodInfo; previous: PeriodInfo }
  previous_available: boolean
  total: { count: number; previous_count: number | null; change_pct: number | null }
  akseldrift: AkseldriftStats[]
  top_models: Record<Drive, TopModel[]>
  top_brands: Record<Drive, TopBrand[]>
}

export interface SeriesValues {
  bucket: string
  count_2wd: number
  count_4wd: number
  share_2wd: number | null
  share_4wd: number | null
}

export interface AkseldriftTidsserie {
  output_mode: string
  periods: { current: PeriodInfo; previous: PeriodInfo }
  previous_available: boolean
  granularity: 'day' | 'month'
  points: (SeriesValues & { previous: SeriesValues | null })[]
}

/** 2WD and 4WD for one brand, model or the TOTAL row */
export interface DriftColumns {
  count_2wd: number
  count_4wd: number
  total: number
  share_2wd: number | null
  share_4wd: number | null
  previous_total: number | null
  share_4wd_change_pp: number | null
  total_change_pct: number | null
}

export interface BrandRow extends DriftColumns {
  brand: string
}

export interface ModelRow extends DriftColumns {
  master_model_id: number
  model: string
  brand: string
}

export interface AkseldriftMerker {
  periods: { current: PeriodInfo; previous: PeriodInfo }
  previous_available: boolean
  summary: DriftColumns
  data: BrandRow[]
}

export interface AkseldriftModeller {
  periods: { current: PeriodInfo; previous: PeriodInfo }
  previous_available: boolean
  both_count: number
  summary: DriftColumns
  data: ModelRow[]
}

export interface SegmentRow extends DriftColumns {
  segment_id: number
  segment: string
}

export interface AkseldriftSegmenter {
  periods: { current: PeriodInfo; previous: PeriodInfo }
  previous_available: boolean
  summary: DriftColumns
  data: SegmentRow[]
}

export interface AgeGroupRow {
  age_group: string
  count_2wd: number
  count_4wd: number
  share_2wd: number | null
  dist_2wd: number | null
  dist_4wd: number | null
}

export interface AkseldriftAlder {
  periods: Periods
  groups: AgeGroupRow[]
  total_2wd: number
  total_4wd: number
  without_age: number
}

// ── Drivstoff (#875): /public/teknisk/drivstoff/* ───────────────────────────
// With ekskluder_elbil every "elbil" key is left out of the response.

/** One per car (backend app/crud/fuel_category.py) */
export type FuelLeaf = 'elbil' | 'ladbar' | 'ikke_ladbar' | 'diesel' | 'bensin' | 'annet'
/** The categories the page shows; hybrid = ladbar + ikke_ladbar */
export type FuelTop = 'elbil' | 'hybrid' | 'diesel' | 'bensin' | 'annet'
/** What «Vis kategori» and «Se alle» pick: a category or one hybrid branch */
export type FuelCategory = FuelTop | HybridLeaf
export type HybridLeaf = 'ladbar' | 'ikke_ladbar'
export type HybridBase = 'bensin' | 'diesel' | 'annet'

export type LeafCounts = Partial<Record<FuelLeaf, number>>

export interface FuelStats {
  count: number
  share: number | null
  previous_count: number | null
  previous_share: number | null
  share_change_pp: number | null
}

export interface FuelCategoryStats extends FuelStats {
  kategori: FuelTop
  leasing_count: number
  leasing_share: number | null
  loan_base: number
  loan_count: number
  loan_share: number | null
  underkategorier: (FuelStats & { kategori: HybridLeaf })[]
}

export interface DrivstoffOversikt {
  output_mode: string
  periods: { current: PeriodInfo; previous: PeriodInfo }
  previous_available: boolean
  ekskluder_elbil: boolean
  total: { count: number; previous_count: number | null; change_pct: number | null }
  kategorier: FuelCategoryStats[]
  top_models: Partial<Record<FuelTop, TopModel[]>>
  top_brands: Partial<Record<FuelTop, TopBrand[]>>
}

interface DrivstoffSeriesValues {
  bucket: string
  total: number
  counts: LeafCounts
  shares: Partial<Record<FuelTop, number | null>>
}

export interface DrivstoffTidsserie {
  periods: { current: PeriodInfo; previous: PeriodInfo }
  previous_available: boolean
  ekskluder_elbil: boolean
  granularity: 'day' | 'month'
  /** previous: the same position in last year's period, null before the data starts */
  points: (DrivstoffSeriesValues & { previous: DrivstoffSeriesValues | null })[]
}

/** Leaf counts for one brand, model or the TOTAL row; the page derives shares */
export interface FuelColumns {
  total: number
  counts: LeafCounts
  previous_total: number | null
  previous_counts: LeafCounts | null
  total_change_pct: number | null
}

export interface FuelBrandRow extends FuelColumns {
  brand: string
}

export interface FuelModelRow extends FuelColumns {
  master_model_id: number
  model: string
  brand: string
}

export interface DrivstoffMerker {
  periods: { current: PeriodInfo; previous: PeriodInfo }
  previous_available: boolean
  ekskluder_elbil: boolean
  summary: FuelColumns
  data: FuelBrandRow[]
}

export interface DrivstoffModeller {
  periods: { current: PeriodInfo; previous: PeriodInfo }
  previous_available: boolean
  ekskluder_elbil: boolean
  summary: FuelColumns
  data: FuelModelRow[]
}

export interface HybridBranch extends FuelStats {
  kategori: HybridLeaf
  basis: (FuelStats & { basis: HybridBase })[]
}

export interface HybridSeriesValues {
  bucket: string
  count_ladbar: number
  count_ikke_ladbar: number
}

export interface DrivstoffHybrid {
  periods: { current: PeriodInfo; previous: PeriodInfo }
  previous_available: boolean
  ladbar: HybridLeaf | null
  basis: HybridBase | null
  market_total: number
  hybrid: FuelStats
  grener: HybridBranch[]
  branch_count: number
  granularity: 'day' | 'month'
  points: (HybridSeriesValues & { previous: HybridSeriesValues | null })[]
  top_brands: TopBrand[]
  top_models: TopModel[]
}

export interface DrivstoffAlder {
  periods: Periods
  ekskluder_elbil: boolean
  groups: { age_group: string; total: number; counts: LeafCounts; shares: Partial<Record<FuelTop, number | null>> }[]
  totals: Partial<Record<FuelTop, number>>
  total_with_age: number
  without_age: number
}

export type TopTab = 'akseldrift' | 'drivstoff'

export type SubTab = 'oversikt' | 'merke' | 'modell' | 'segment' | 'hybrid' | 'alder'

export interface AkseldriftQuery {
  output_mode: string
  varebil?: boolean
  mine_postnummer?: string
  custom_start_date?: string
  custom_end_date?: string
}
